import { jsx } from "react/jsx-runtime";
import { css } from "@/styled-system/css";
import { Table } from "@zillow/constellation-10";
import { useId } from "react";
import makeContentInteractive from "../../util/makeSpansInteractive.esm.js";
import { getHeaderAssociations, getCellAlignments, toSpan, hasBodyRowspan } from "./tableGrid.esm.js";
const APPEARANCE_ALIASES = {
  barebones: "bare"
};
const APPEARANCES = ["bare", "horizontal", "grid", "zebra"];
const SIZES = ["sm", "md", "lg"];
const isOneOf = (values, value) => values.includes(value);
const resolveAppearance = (appearance) => {
  const mapped = APPEARANCE_ALIASES[appearance ?? ""] ?? appearance;
  return isOneOf(APPEARANCES, mapped) ? mapped : "grid";
};
const resolveSize = (size) => isOneOf(SIZES, size) ? size : "md";
const resolveStickyHeader = ({
  stickyHeader,
  head
}) => !!stickyHeader && !!(head == null ? void 0 : head.length);
const resolveResponsiveStyles = (sticky) => sticky ? css.raw({
  maxHeight: "80vh"
}) : void 0;
const resolveAlignments = ({
  body,
  head
}) => {
  var _a, _b;
  const template = ((_a = body == null ? void 0 : body[0]) == null ? void 0 : _a.cells) ?? ((_b = head == null ? void 0 : head[0]) == null ? void 0 : _b.cells);
  if (!template) {
    return void 0;
  }
  return template.map((cell) => (cell == null ? void 0 : cell.align) || "left");
};
const resolveHeaderStyles = (sticky, rowCount) => sticky && rowCount > 1 ? css.raw({
  position: "sticky",
  top: 0,
  zIndex: 1
}) : void 0;
const resolveTableAppearance = ({
  appearance,
  body
}) => {
  const resolved = resolveAppearance(appearance);
  return resolved === "zebra" && hasBodyRowspan(body) ? "grid" : resolved;
};
const TSection = ({
  name,
  sticky,
  rowCount,
  children
}) => {
  if (name === "body") {
    return /* @__PURE__ */ jsx(Table.Body, { children });
  }
  const headerStyles = resolveHeaderStyles(sticky, rowCount);
  return /* @__PURE__ */ jsx(Table.Header, { sticky: sticky && !headerStyles, css: headerStyles, children });
};
const TCell = ({
  name,
  tag,
  cellAlign,
  align,
  children,
  ...rest
}) => name === "head" || tag === "th" ? /* @__PURE__ */ jsx(Table.HeaderCell, { align: cellAlign ?? align, ...rest, children }) : /* @__PURE__ */ jsx(Table.Cell, { align: cellAlign ?? align, ...rest, children });
const SECTIONS = ["head", "body"];
const BlockTable = ({
  attrs
}) => {
  const {
    head,
    body,
    appearance,
    size,
    stickyHeader,
    ariaLabel,
    ...otherProps
  } = attrs;
  const sticky = resolveStickyHeader({
    stickyHeader,
    head
  });
  const rowsBySection = {
    head,
    body
  };
  const idPrefix = `table-${useId().replace(/:/g, "")}`;
  const associations = getHeaderAssociations({
    head,
    body
  }, idPrefix);
  const alignments = getCellAlignments({
    head,
    body
  });
  return /* @__PURE__ */ jsx(Table.Responsive, { css: resolveResponsiveStyles(sticky), children: /* @__PURE__ */ jsx(
    Table.Root,
    {
      alignments: resolveAlignments(attrs),
      appearance: resolveTableAppearance({
        appearance,
        body
      }),
      "aria-label": ariaLabel || void 0,
      size: resolveSize(size),
      ...otherProps,
      children: SECTIONS.map((name) => {
        var _a, _b;
        return /* @__PURE__ */ jsx(TSection, { name, sticky, rowCount: ((_a = rowsBySection[name]) == null ? void 0 : _a.length) ?? 0, children: (_b = rowsBySection[name]) == null ? void 0 : _b.map(({
          cells
        }, rowIndex) => /* @__PURE__ */ jsx(Table.Row, { children: cells.map(({
          content,
          tag,
          colspan,
          rowspan,
          scope
        }, columnIndex) => /* @__PURE__ */ jsx(TCell, { name, tag, colSpan: toSpan(colspan) > 1 ? toSpan(colspan) : void 0, rowSpan: toSpan(rowspan) > 1 ? toSpan(rowspan) : void 0, scope, cellAlign: alignments[name][rowIndex][columnIndex], ...associations == null ? void 0 : associations[name][rowIndex][columnIndex], children: makeContentInteractive(content) }, columnIndex)) }, rowIndex)) }, name);
      })
    }
  ) });
};
export {
  BlockTable as default,
  resolveAlignments,
  resolveAppearance,
  resolveHeaderStyles,
  resolveResponsiveStyles,
  resolveSize,
  resolveStickyHeader,
  resolveTableAppearance
};
